import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderStatusHistoryResponse } from '@app/utils/interfaces/order.interface';
import { TranslatePipe } from '@ngx-translate/core';
import { OrdersService } from '../../services/orders.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-order-status-history',
  standalone: true,
  imports: [TranslatePipe, DatePipe, MatProgressSpinnerModule],
  templateUrl: './order-status-history.component.html',
  styleUrl: './order-status-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderStatusHistoryComponent implements OnInit {
  private readonly orderService = inject(OrdersService);
  private readonly destroyRef = inject(DestroyRef);

  protected isLoading = signal(false);

  public historyResponse = signal<OrderStatusHistoryResponse | null>(null);
  public orderId = input.required<number>();
  public errorMessage = signal<boolean>(false);

  public formattedHistory = computed(() => {
    const rawData = this.historyResponse()?.data || [];

    return rawData.map((log) => ({
      ...log,
      statusClass: log.status
        ? log.status
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()
            .replace(/^-/, '')
        : '',
      statusLabel: log.status ? log.status.replace(/([A-Z])/g, ' $1').trim() : '',
    }));
  });

  public ngOnInit(): void {
    this.loadHistory();
  }

  private loadHistory(): void {
    this.isLoading.set(true);
    this.errorMessage.set(false);

    this.orderService
      .getOrderStatus(this.orderId())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((res) => {
          this.historyResponse.set(res);
        }),
        catchError((err: HttpErrorResponse) => {
          if(err.status === 0 && !navigator.onLine) {
            this.errorMessage.set(true);
          }

          this.historyResponse.set(null);
          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();
  }
}
